// The morning and the evening as lists (B27, B32): steps with minutes and a condition, and per day type a start
// time. The blocks of the groups "Ochtend" and "Avond" in each day type are made from them, so each has one source
// (L30).

import type { SlotKind } from "./rhythm";
import { minutesToTime, timeToMinutes } from "./time";

export type RoutinePart = "morning" | "evening";

/** The group each list fills: the name its steps carry on Vandaag. */
export const PART_GROUP: Record<RoutinePart, string> = { morning: "Ochtend", evening: "Avond" };
export const MORNING_GROUP = PART_GROUP.morning;
export const EVENING_GROUP = PART_GROUP.evening;
/** Groups that are edited in their list in Meer, never block by block in the Ritme editor. */
export const LIST_GROUPS = [MORNING_GROUP, EVENING_GROUP];

export function partOfGroup(group: string | null | undefined): RoutinePart | null {
  const g = group?.trim().toLowerCase();
  if (g === MORNING_GROUP.toLowerCase()) return "morning";
  if (g === EVENING_GROUP.toLowerCase()) return "evening";
  return null;
}

export interface MorningStepLike {
  id: string;
  title: string;
  minutes: number;
  position: number;
  template_ids: string[] | null;
  slot_kind: SlotKind;
  notify: boolean;
  part?: RoutinePart;
  is_fixed?: boolean;
  deleted_at?: string | Date | null;
}

export interface MorningBlock {
  title: string;
  slot_kind: SlotKind;
  start_local: string;
  notify: boolean;
  is_fixed: boolean;
  position: number;
}

/** Does a step belong on this day type? Null means every day type with this part. */
export function appliesTo(step: Pick<MorningStepLike, "template_ids">, templateId: string): boolean {
  return !step.template_ids || step.template_ids.includes(templateId);
}

const inPart = (part: RoutinePart) => (s: MorningStepLike) => (s.part ?? "morning") === part;

/** The blocks of a part on a day type: its start time plus the minutes of the steps before. None without a start. */
export function morningBlocks(
  steps: MorningStepLike[],
  templateId: string,
  start: string | null,
  part: RoutinePart = "morning",
): MorningBlock[] {
  if (!start) return [];
  let at = timeToMinutes(start);
  return steps
    .filter((s) => !s.deleted_at && inPart(part)(s) && appliesTo(s, templateId))
    .sort((a, b) => a.position - b.position)
    .map((s, position) => {
      const block = {
        title: s.title,
        slot_kind: s.slot_kind,
        start_local: `${minutesToTime(Math.min(at, 23 * 60 + 59))}:00`,
        notify: s.notify,
        is_fixed: s.is_fixed ?? false,
        position,
      };
      at += s.minutes;
      return block;
    });
}

/** When a part of a day type is done: its start plus all its minutes. */
export function morningEnd(
  steps: MorningStepLike[],
  templateId: string,
  start: string | null,
  part: RoutinePart = "morning",
): string | null {
  if (!start) return null;
  const total = steps.filter((s) => !s.deleted_at && inPart(part)(s) && appliesTo(s, templateId)).reduce((sum, s) => sum + s.minutes, 0);
  return minutesToTime(Math.min(timeToMinutes(start) + total, 23 * 60 + 59));
}

/** A step that would start at the very time of another block of that day type (one block per start time). */
export function morningClash(
  blocks: MorningBlock[],
  others: { title: string; start_local: string }[],
): { step: string; block: string } | null {
  for (const [i, b] of blocks.entries()) {
    // Late in the evening every step stops at 23:59: two of them on the same time.
    const twin = blocks.slice(0, i).find((x) => x.start_local === b.start_local);
    if (twin) return { step: b.title, block: twin.title };
    const other = others.find((o) => timeToMinutes(o.start_local) === timeToMinutes(b.start_local));
    if (other) return { step: b.title, block: other.title };
  }
  return null;
}

interface TemplateBlockLike {
  title: string;
  slot_kind: SlotKind;
  start_local: string;
  group_title: string | null;
  notify: boolean;
  is_fixed?: boolean;
  position: number;
}

export interface DerivedMorning {
  steps: Omit<MorningStepLike, "id">[];
  starts: Map<string, string | null>;
}

type Picker = (sorted: TemplateBlockLike[]) => TemplateBlockLike[];

/** The morning group as it stands in a day type. */
const pickMorning: Picker = (sorted) => sorted.filter((b) => b.group_title === MORNING_GROUP);

/**
 * The evening as it stands in a day type before it was a group (B32): from the planning block up to and with
 * bedtime, the blocks in no other group. Already a group: that group.
 */
export const pickEvening: Picker = (sorted) => {
  const grouped = sorted.filter((b) => b.group_title === EVENING_GROUP);
  if (grouped.length) return grouped;
  const from = sorted.findIndex((b) => b.slot_kind === "planning" && !b.group_title);
  if (from < 0) return [];
  const out: TemplateBlockLike[] = [];
  for (const b of sorted.slice(from)) {
    if (b.group_title) continue;
    out.push(b);
    if (b.slot_kind === "sleep") break;
  }
  return out;
};

/**
 * The list from the groups as they stand in the day types (moving over to the list, B27, B32). The order merges
 * the day types (first one first); a step's minutes are the gap to the next step where it is not the last one, and
 * a step found in every day type with this part counts for all of them.
 */
function derive(templates: { id: string; blocks: TemplateBlockLike[] }[], pick: Picker, part: RoutinePart): DerivedMorning {
  const starts = new Map<string, string | null>();
  const order: string[] = [];
  const found = new Map<string, { template: string; block: TemplateBlockLike; gap: number | null; last: boolean }[]>();
  for (const t of templates) {
    const sorted = [...t.blocks].sort((a, b) => timeToMinutes(a.start_local) - timeToMinutes(b.start_local) || a.position - b.position);
    const group = pick(sorted);
    starts.set(t.id, group[0] ? group[0].start_local.slice(0, 5) : null);
    group.forEach((b, i) => {
      const next = group[i + 1] ?? sorted.find((x) => !group.includes(x) && timeToMinutes(x.start_local) > timeToMinutes(b.start_local));
      const gap = next ? timeToMinutes(next.start_local) - timeToMinutes(b.start_local) : null;
      found.set(b.title, [...(found.get(b.title) ?? []), { template: t.id, block: b, gap, last: i === group.length - 1 }]);
      if (!order.includes(b.title)) {
        const before = group[i - 1] ? order.indexOf(group[i - 1]!.title) : -1;
        order.splice(before + 1, 0, b.title);
      }
    });
  }
  const withPart = templates.filter((t) => starts.get(t.id));
  const steps = order.map((title, position) => {
    const seen = found.get(title)!;
    const first = seen[0]!;
    const gap = seen.find((s) => !s.last && s.gap)?.gap ?? first.gap ?? 15;
    const everywhere = withPart.every((t) => seen.some((s) => s.template === t.id));
    return {
      title,
      minutes: Math.max(1, Math.min(gap, 240)),
      position,
      template_ids: everywhere ? null : seen.map((s) => s.template),
      slot_kind: first.block.slot_kind,
      notify: first.block.notify,
      is_fixed: first.block.is_fixed ?? false,
      part,
    };
  });
  return { steps, starts };
}

export function deriveMorning(templates: { id: string; blocks: TemplateBlockLike[] }[]): DerivedMorning {
  return derive(templates, pickMorning, "morning");
}

export function deriveEvening(templates: { id: string; blocks: TemplateBlockLike[] }[]): DerivedMorning {
  return derive(templates, pickEvening, "evening");
}
